import { useEffect, useReducer, useCallback } from 'react';

const ACTIONS = {
  SET_ENTITY_TYPES: 'SET_ENTITY_TYPES',
  LOAD_MORE: 'LOAD_MORE',
  SEARCH_CHANGE: 'SEARCH_CHANGE',
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  MANUAL_ADD_LINKED_ENTITIES: 'MANUAL_ADD_LINKED_ENTITIES',
  REMOVE_RELATIONSHIP_ID: 'REMOVE_RELATIONSHIP_ID',
  NEW_ENTITY_CREATED: 'NEW_ENTITY_CREATED',
  RESET_OFFSET: 'RESET_OFFSET'
};

const initialTypeState = entityType => ({
  entityType,
  isLoading: false,
  entities: {}
});

const dataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_ENTITY_TYPES:
      return {
        offset: 0,
        manuallyAddedRelationshipIds: [],
        entitiesByType: action.payload.reduce((acc, entityType) => {
          acc[entityType] = initialTypeState(entityType);
          return acc;
        }, {})
      };
    case ACTIONS.LOAD_MORE:
      return {
        ...state,
        offset: state.offset + action.payload.size
      };
    case ACTIONS.FETCH_INIT:
      return {
        ...state,
        entitiesByType: {
          ...state.entitiesByType,
          [action.payload.entityType]: {
            ...state.entitiesByType[action.payload.entityType],
            isLoading: true,
            error: null,
            onTryAgain: null
          }
        }
      };
    case ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        entitiesByType: {
          ...state.entitiesByType,
          [action.payload.entityType]: {
            ...state.entitiesByType[action.payload.entityType],
            isLoading: false,
            error: null,
            onTryAgain: null,
            entities: action.payload.entities.reduce(
              (acc, entity) => {
                acc[entity.id] = entity;
                return acc;
              },
              { ...state.entitiesByType[action.payload.entityType].entities }
            )
          }
        }
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...state,
        entitiesByType: {
          ...state.entitiesByType,
          [action.payload.entityType]: {
            ...state.entitiesByType[action.payload.entityType],
            isLoading: false,
            error: action.payload.error,
            onTryAgain: action.payload.onTryAgain
          }
        }
      };
    case ACTIONS.NEW_ENTITY_CREATED: {
      const newEntitiesByType = { ...state.entitiesByType };
      const { entity } = action.payload;
      if (!newEntitiesByType[entity.entityType]) {
        newEntitiesByType[entity.entityType] = {};
      }
      newEntitiesByType[entity.entityType].entities = {
        ...newEntitiesByType[entity.entityType].entities,
        [entity.id]: entity
      };
      return {
        ...state,
        entitiesByType: newEntitiesByType
      };
    }
    case ACTIONS.MANUAL_ADD_LINKED_ENTITIES: {
      const newEntitiesByType = { ...state.entitiesByType };
      action.payload.entities.forEach(entity => {
        if (!newEntitiesByType[entity.entityType]) {
          newEntitiesByType[entity.entityType] = {};
        }
        newEntitiesByType[entity.entityType].entities = action.payload.entities.reduce(
          (acc, entity) => {
            acc[entity.id] = entity;
            return acc;
          },
          { ...newEntitiesByType[entity.entityType].entities }
        );
      });
      return {
        ...state,
        manuallyAddedRelationshipIds: [
          ...action.payload.relationships.map(r => r.id),
          ...state.manuallyAddedRelationshipIds
        ],
        entitiesByType: newEntitiesByType
      };
    }
    case ACTIONS.REMOVE_RELATIONSHIP_ID:
      return {
        ...state,
        manuallyAddedRelationshipIds: state.manuallyAddedRelationshipIds.filter(
          id => id !== action.payload
        )
      };
    case ACTIONS.RESET_OFFSET:
      return {
        ...state,
        offset: 0
      };
    default:
      throw new Error();
  }
};

export default ({ entityTypes, typeToPlugin, relationships = [], pageLimit = 5, autoLoadMore }) => {
  const [state, dispatch] = useReducer(dataReducer, {
    entitiesByType: {},
    offset: 0,
    manuallyAddedRelationshipIds: []
  });
  const { offset } = state;

  useEffect(() => {
    dispatch({ type: ACTIONS.SET_ENTITY_TYPES, payload: entityTypes });
  }, [entityTypes]);

  useEffect(() => {
    if (relationships.length) {
      dispatch({ type: ACTIONS.RESET_OFFSET });
    }
  }, [relationships]);

  useEffect(() => {
    if (relationships && relationships.length && typeToPlugin) {
      const relationshipsByType = {};
      for (let i = offset; i < Math.min(offset + pageLimit, relationships.length); i++) {
        const rel = relationships[i];
        const type = rel.entityType;
        if (!relationshipsByType[type]) {
          relationshipsByType[type] = [];
        }
        relationshipsByType[type].push(rel);
      }
      Object.keys(relationshipsByType).forEach(entityType => {
        const plugin = typeToPlugin[entityType];
        const readData = ids => {
          if (ids.length) {
            dispatch({ type: ACTIONS.FETCH_INIT, payload: { entityType }});
            return plugin
              .getEntitiesByIds(ids)
              .then(({ data: entities }) => {
                dispatch({
                  type: ACTIONS.FETCH_SUCCESS,
                  payload: {
                    entityType,
                    entities
                  }
                });
              })
              .catch(error => {
                console.error('Error getting entities', error);
                dispatch({
                  type: ACTIONS.FETCH_FAILURE,
                  payload: {
                    entityType,
                    error,
                    onTryAgain: () => readData(ids)
                  }
                });
              });
          }
          return null;
        };
        const ids = (relationshipsByType[entityType] || [])
          .map(r => r.entityId)
          .filter((v, i, a) => a.indexOf(v) === i)
          .filter(
            id =>
              !(state.entitiesByType[entityType] && state.entitiesByType[entityType].entities[id])
          );
        readData(ids);
      });
    }
    // No need to refetch on `entitiesByType` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeToPlugin, relationships, offset, pageLimit]);

  const mapRelationshipToEntity = ({ entityId, entityType, ...relationship }) =>
    state.entitiesByType[entityType] && state.entitiesByType[entityType].entities[entityId]
      ? {
        ...relationship,
        entity: state.entitiesByType[entityType].entities[entityId]
      }
      : null;
  const slicedRelationships = relationships
    .slice(0, offset + pageLimit)
    .filter(({ relationship }) => !state.manuallyAddedRelationshipIds.includes(relationship.id));
  const manualAddedRelationshipToAddFirst = state.manuallyAddedRelationshipIds
    .map(id => relationships.find(({ relationship }) => relationship.id === id))
    .filter(e => e);
  const entities = [...manualAddedRelationshipToAddFirst, ...slicedRelationships]
    .map(mapRelationshipToEntity)
    .filter(e => e);

  const onLoadMoreCb = useCallback(() => {
    dispatch({ type: ACTIONS.LOAD_MORE, payload: { size: pageLimit }});
  }, [pageLimit]);
  const hasMore =
    offset + pageLimit + manualAddedRelationshipToAddFirst.length < relationships.length;
  const onLoadMore = hasMore ? onLoadMoreCb : undefined;

  useEffect(() => {
    autoLoadMore && onLoadMore && onLoadMore();
  });

  const addEntity = entity => {
    dispatch({ type: ACTIONS.NEW_ENTITY_CREATED, payload: { entity }});
  };

  const addLinkedEntities = (relationships, entities) => {
    dispatch({ type: ACTIONS.MANUAL_ADD_LINKED_ENTITIES, payload: { relationships, entities }});
  };

  const removeLinkedEntityRelationship = relationshipsId => {
    dispatch({ type: ACTIONS.REMOVE_RELATIONSHIP_ID, payload: relationshipsId });
  };

  const isLoading = !!Object.values(state.entitiesByType).find(s => s.isLoading);
  const error = !!Object.values(state.entitiesByType).find(s => s.error);
  const retry = error
    ? () => Object.values(state.entitiesByType).forEach(s => s.error && s.onTryAgain())
    : undefined;

  return {
    entities,
    isLoading,
    error,
    retry,
    onLoadMore,
    addEntity,
    addLinkedEntities,
    removeLinkedEntityRelationship
  };
};
